const STATE_KEY = "hydrotools:patch-test:v1";
const BINARY_EXTENSIONS = new Set(["gsf", "hsx", "all", "kmall", "s7k", "xtf", "raw"]);

export const patchTestTool = {
  id: "patch-test",
  name: "Patch Test",
  init(app) { this._rt = this._rt || createRuntime(app); },
  destroy() { this._rt?.destroy(); this._rt = null; },
  renderPanel(app, containerEl) { if (!this._rt) this._rt = createRuntime(app); this._rt.mount(containerEl); },
  serialize() { return this._rt?.snapshot() || null; },
  restore(app, data) { if (!this._rt) this._rt = createRuntime(app); this._rt.restore(data); },
};

function createRuntime(app) {
  let containerEl = null;
  let busy = false;
  let status = "Load raw sonar or exported diagnostic points to begin.";
  let points = [];
  let files = [];
  let results = null;
  let converterStatus = null;
  let state = readState();

  function readState() {
    try {
      const raw = localStorage.getItem(STATE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return sanitizeState(parsed);
    } catch {
      return sanitizeState({});
    }
  }

  function sanitizeState(value) {
    const source = value && typeof value === "object" ? value : {};
    return {
      cellSize: clampNum(source.cellSize, 0.05, 5000, 1),
      maxPoints: clampNum(source.maxPoints, 1000, 1000000, 150000),
      robustTrimPct: clampNum(source.robustTrimPct, 0, 45, 10),
      coarseRange: clampNum(source.coarseRange, 0.1, 45, 10),
      coarseStep: clampNum(source.coarseStep, 0.01, 10, 1),
      fineRange: clampNum(source.fineRange, 0.01, 10, 1),
      fineStep: clampNum(source.fineStep, 0.001, 2, 0.1),
      finalRange: clampNum(source.finalRange, 0.001, 1, 0.05),
      finalStep: clampNum(source.finalStep, 0.0001, 0.5, 0.005),
    };
  }

  function persist() {
    try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch {}
  }

  function esc(v) {
    return String(v ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll("\"", "&quot;");
  }

  function clampNum(value, min, max, fallback) {
    const n = Number(value);
    if (!Number.isFinite(n)) return fallback;
    return Math.max(min, Math.min(max, n));
  }

  function fmt(value, digits = 4) {
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(digits).replace(/\.?0+$/, "") : "n/a";
  }

  function readInputs() {
    if (!containerEl) return;
    state = sanitizeState({
      cellSize: containerEl.querySelector("#pt-cell-size")?.value,
      maxPoints: containerEl.querySelector("#pt-max-points")?.value,
      robustTrimPct: containerEl.querySelector("#pt-trim")?.value,
      coarseRange: containerEl.querySelector("#pt-coarse-range")?.value,
      coarseStep: containerEl.querySelector("#pt-coarse-step")?.value,
      fineRange: containerEl.querySelector("#pt-fine-range")?.value,
      fineStep: containerEl.querySelector("#pt-fine-step")?.value,
      finalRange: containerEl.querySelector("#pt-final-range")?.value,
      finalStep: containerEl.querySelector("#pt-final-step")?.value,
    });
    persist();
  }

  async function loadFiles() {
    const input = containerEl?.querySelector?.("#pt-files");
    const selected = Array.from(input?.files || []);
    if (!selected.length) {
      status = "Choose at least two patch-test line files.";
      render();
      return;
    }
    readInputs();
    busy = true;
    status = `Reading ${selected.length} file(s)...`;
    render();
    try {
      files = [];
      points = [];
      results = null;
      let skippedBinary = 0;
      for (const file of selected) {
        const ext = String(file.name || "").split(".").pop().toLowerCase();
        const record = { name: file.name, size: file.size, ext, status: "loaded", points: 0 };
        if (BINARY_EXTENSIONS.has(ext)) {
          record.status = "staged binary";
          record.note = "Needs server-side converter before optimization.";
          files.push(record);
          skippedBinary++;
          continue;
        }
        const text = await file.text();
        const parsed = parsePointText(text, file.name);
        record.points = parsed.length;
        record.status = parsed.length ? "parsed" : "no points";
        files.push(record);
        points.push(...parsed);
      }
      if (points.length > state.maxPoints) points = decimate(points, state.maxPoints);
      deriveGeometry(points);
      const lineCount = new Set(points.map((p) => p.lineId)).size;
      const binaryNote = skippedBinary ? ` ${skippedBinary} hydrographic binary file(s) are staged but not parsed yet.` : "";
      status = points.length
        ? `Loaded ${points.length.toLocaleString()} point(s) across ${lineCount} line(s) for an XYZ residual diagnostic.${binaryNote}`
        : `No parseable diagnostic points found.${binaryNote} Use Qimera/raw ingest for true patch testing.`;
      app?.log?.("patch-test.load", { files: files.length, points: points.length, lineCount });
    } catch (error) {
      status = String(error?.message || error);
    } finally {
      busy = false;
      render();
    }
  }

  function parsePointText(text, fileName) {
    const trimmed = String(text || "").trim();
    if (!trimmed) return [];
    if (/^[\[{]/.test(trimmed)) return parseJsonPoints(trimmed, fileName);
    return parseDelimitedPoints(trimmed, fileName);
  }

  function parseJsonPoints(text, fileName) {
    try {
      const data = JSON.parse(text);
      const rows = Array.isArray(data) ? data : Array.isArray(data?.points) ? data.points : [];
      return rows.map((row, idx) => normalizePoint(row, fileName, idx)).filter(Boolean);
    } catch {
      return [];
    }
  }

  function parseDelimitedPoints(text, fileName) {
    const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (!lines.length) return [];
    const first = splitDelimited(lines[0]);
    const hasHeader = first.some((part) => /[a-z_]/i.test(part));
    const headers = hasHeader ? first.map((part) => normalizeHeader(part)) : [];
    const body = hasHeader ? lines.slice(1) : lines;
    const out = [];
    for (let i = 0; i < body.length; i++) {
      const cols = splitDelimited(body[i]);
      if (cols.length < 3) continue;
      const row = hasHeader ? Object.fromEntries(headers.map((h, idx) => [h, cols[idx]])) : cols;
      const point = normalizePoint(row, fileName, i);
      if (point) out.push(point);
    }
    return out;
  }

  function splitDelimited(line) {
    return String(line || "").split(/[,\t; ]+/).map((part) => part.trim()).filter((part) => part.length);
  }

  function normalizeHeader(value) {
    return String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  }

  function pickNumber(row, keys, fallbackIndex) {
    if (Array.isArray(row)) return Number(row[fallbackIndex]);
    for (const key of keys) {
      const value = row[key];
      const n = Number(value);
      if (Number.isFinite(n)) return n;
    }
    return NaN;
  }

  function pickString(row, keys, fallback) {
    if (Array.isArray(row)) return fallback;
    for (const key of keys) {
      const value = row[key];
      if (value !== undefined && value !== null && String(value).trim()) return String(value).trim();
    }
    return fallback;
  }

  function normalizePoint(row, fileName, idx) {
    const x = pickNumber(row, ["x", "easting", "east", "lon", "longitude"], 0);
    const y = pickNumber(row, ["y", "northing", "north", "lat", "latitude"], 1);
    const z = pickNumber(row, ["z", "depth", "elevation", "height", "sound_depth"], 2);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null;
    const lineId = pickString(row, ["line", "line_id", "lineid", "track", "track_id", "file"], fileStem(fileName));
    const along = pickNumber(row, ["along", "alongtrack", "along_track", "distance", "station"], 3);
    const across = pickNumber(row, ["across", "acrosstrack", "across_track", "beam_x", "offset"], 4);
    return {
      id: `${lineId}:${idx}`,
      lineId,
      x,
      y,
      z,
      along: Number.isFinite(along) ? along : null,
      across: Number.isFinite(across) ? across : null,
    };
  }

  function fileStem(name) {
    return String(name || "line").replace(/\.[^.]+$/, "") || "line";
  }

  function decimate(items, max) {
    const stride = Math.max(1, Math.ceil(items.length / max));
    return items.filter((_, idx) => idx % stride === 0);
  }

  function deriveGeometry(items) {
    const groups = new Map();
    for (const point of items) {
      if (!groups.has(point.lineId)) groups.set(point.lineId, []);
      groups.get(point.lineId).push(point);
    }
    for (const group of groups.values()) {
      const axes = deriveLineAxes(group);
      for (const point of group) {
        const dx = point.x - axes.cx;
        const dy = point.y - axes.cy;
        if (!Number.isFinite(point.along)) point.along = dx * axes.ax + dy * axes.ay;
        if (!Number.isFinite(point.across)) point.across = dx * axes.bx + dy * axes.by;
        point.cx = axes.cx;
        point.cy = axes.cy;
      }
    }
  }

  function deriveLineAxes(group) {
    const n = group.length || 1;
    const cx = group.reduce((sum, p) => sum + p.x, 0) / n;
    const cy = group.reduce((sum, p) => sum + p.y, 0) / n;
    let sxx = 0;
    let sxy = 0;
    let syy = 0;
    for (const p of group) {
      const dx = p.x - cx;
      const dy = p.y - cy;
      sxx += dx * dx;
      sxy += dx * dy;
      syy += dy * dy;
    }
    const theta = 0.5 * Math.atan2(2 * sxy, sxx - syy);
    const ax = Math.cos(theta);
    const ay = Math.sin(theta);
    return { cx, cy, ax, ay, bx: -ay, by: ax };
  }

  async function runOptimization() {
    readInputs();
    if (busy) return;
    const lineCount = new Set(points.map((p) => p.lineId)).size;
    if (points.length < 100 || lineCount < 2) {
      status = "Load at least two overlapping line point sets first. This is an XYZ residual diagnostic, not a true sensor patch test.";
      render();
      return;
    }
    busy = true;
    status = "Running XYZ residual diagnostic scans...";
    results = null;
    render();
    await new Promise((resolve) => setTimeout(resolve, 20));
    try {
      const axes = ["roll", "pitch", "yaw"];
      const axisResults = [];
      const baseOffsets = { roll: 0, pitch: 0, yaw: 0 };
      for (const axis of axes) {
        const scans = [];
        let center = 0;
        for (const pass of [
          { name: "coarse", range: state.coarseRange, step: state.coarseStep },
          { name: "fine", range: state.fineRange, step: state.fineStep },
          { name: "final", range: state.finalRange, step: state.finalStep },
        ]) {
          const scan = scanAxis(axis, center, pass.range, pass.step, baseOffsets);
          scan.name = pass.name;
          scans.push(scan);
          center = scan.best?.angle ?? center;
        }
        baseOffsets[axis] = center;
        axisResults.push({ axis, angle: center, scans });
      }
      const finalScore = scoreOffsets(baseOffsets);
      results = { axisResults, offsets: baseOffsets, finalScore };
      status = `XYZ diagnostic complete. Apparent offsets: roll ${fmt(baseOffsets.roll)} deg, pitch ${fmt(baseOffsets.pitch)} deg, yaw ${fmt(baseOffsets.yaw)} deg. Use raw-sonar ingest for calibration-grade results.`;
      app?.log?.("patch-test.optimize", { offsets: baseOffsets, score: finalScore });
    } catch (error) {
      status = String(error?.message || error);
    } finally {
      busy = false;
      render();
    }
  }

  function scanAxis(axis, center, range, step, baseOffsets) {
    const samples = [];
    const count = Math.max(1, Math.floor((range * 2) / step));
    const start = center - range;
    for (let i = 0; i <= count; i++) {
      const angle = roundAngle(start + i * step);
      const offsets = { ...baseOffsets, [axis]: angle };
      const score = scoreOffsets(offsets);
      samples.push({ angle, ...score });
    }
    samples.sort((a, b) => a.angle - b.angle);
    const best = samples.reduce((acc, row) => !acc || row.score < acc.score ? row : acc, null);
    return { axis, center, range, step, best, samples };
  }

  function roundAngle(value) {
    return Math.round(Number(value) * 1000000) / 1000000;
  }

  function scoreOffsets(offsets) {
    const cells = new Map();
    const cellSize = Number(state.cellSize) || 1;
    for (const point of points) {
      const adjusted = applyOffsets(point, offsets);
      const ix = Math.floor(adjusted.x / cellSize);
      const iy = Math.floor(adjusted.y / cellSize);
      const key = `${ix}:${iy}`;
      let cell = cells.get(key);
      if (!cell) {
        cell = new Map();
        cells.set(key, cell);
      }
      let line = cell.get(point.lineId);
      if (!line) {
        line = { sum: 0, count: 0 };
        cell.set(point.lineId, line);
      }
      line.sum += adjusted.z;
      line.count++;
    }
    const residuals = [];
    let overlapCells = 0;
    for (const cell of cells.values()) {
      if (cell.size < 2) continue;
      const means = Array.from(cell.values()).map((line) => line.sum / Math.max(1, line.count));
      const mean = means.reduce((sum, value) => sum + value, 0) / means.length;
      for (const value of means) residuals.push(value - mean);
      overlapCells++;
    }
    return summarizeResiduals(residuals, overlapCells);
  }

  function applyOffsets(point, offsets) {
    const roll = degToRad(offsets.roll || 0);
    const pitch = degToRad(offsets.pitch || 0);
    const yaw = degToRad(offsets.yaw || 0);
    const z = point.z + Math.tan(roll) * point.across + Math.tan(pitch) * point.along;
    if (!yaw) return { x: point.x, y: point.y, z };
    const dx = point.x - point.cx;
    const dy = point.y - point.cy;
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    return {
      x: point.cx + dx * c - dy * s,
      y: point.cy + dx * s + dy * c,
      z,
    };
  }

  function degToRad(deg) {
    return Number(deg) * Math.PI / 180;
  }

  function summarizeResiduals(values, overlapCells) {
    if (!values.length) return { score: Number.POSITIVE_INFINITY, rmse: null, mad: null, stdev: null, residuals: 0, overlapCells };
    const sorted = values.slice().sort((a, b) => a - b);
    const trim = Math.floor(sorted.length * (state.robustTrimPct / 100));
    const trimmed = sorted.slice(trim, Math.max(trim + 1, sorted.length - trim));
    const mean = trimmed.reduce((sum, value) => sum + value, 0) / trimmed.length;
    const variance = trimmed.reduce((sum, value) => sum + (value - mean) ** 2, 0) / trimmed.length;
    const rmse = Math.sqrt(trimmed.reduce((sum, value) => sum + value * value, 0) / trimmed.length);
    const median = sorted[Math.floor(sorted.length / 2)];
    const deviations = sorted.map((value) => Math.abs(value - median)).sort((a, b) => a - b);
    const mad = deviations[Math.floor(deviations.length / 2)] * 1.4826;
    return { score: rmse, rmse, mad, stdev: Math.sqrt(variance), residuals: trimmed.length, overlapCells };
  }

  function exportResults() {
    if (!results) return;
    const lines = ["axis,pass,angle_deg,rmse,mad,stdev,overlap_cells,residuals"];
    for (const axis of results.axisResults) {
      for (const scan of axis.scans) {
        for (const sample of scan.samples) {
          lines.push([
            axis.axis,
            scan.name,
            sample.angle,
            sample.rmse ?? "",
            sample.mad ?? "",
            sample.stdev ?? "",
            sample.overlapCells ?? 0,
            sample.residuals ?? 0,
          ].join(","));
        }
      }
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `patch-test-results-${new Date().toISOString().replaceAll(":", "").slice(0, 15)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function resetData() {
    points = [];
    files = [];
    results = null;
    status = "Cleared patch-test data.";
    render();
  }


  async function checkConverterStatus() {
    busy = true;
    status = "Checking Qimera/raw converter availability...";
    render();
    try {
      const response = await fetch("/api/qimera/status", { method: "GET" });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.ok === false) throw new Error(String(payload?.error || `HTTP ${response.status}`));
      converterStatus = payload;
      const q = payload?.qimera || payload;
      const path = q?.executablePath || q?.qimeraCommandPath || q?.path || "";
      status = path
        ? "Qimera bridge is available for ALL/KMALL/S7K project import. GSF/HSX/XTF/RAW still need dedicated decoder support."
        : "Qimera-command is not installed or configured on this host. Install/point to Qimera-command, or add MB-System/native decoders before true patch testing.";
    } catch (error) {
      converterStatus = { ok: false, error: String(error?.message || error) };
      status = String(error?.message || error);
    } finally {
      busy = false;
      render();
    }
  }

  function onClick(event) {
    const button = event.target?.closest?.("button");
    if (!button) return;
    if (button.id === "pt-load") loadFiles();
    if (button.id === "pt-run") runOptimization();
    if (button.id === "pt-export") exportResults();
    if (button.id === "pt-clear") resetData();
    if (button.id === "pt-check-converters") checkConverterStatus();
  }

  function render() {
    if (!containerEl) return;
    const lineCount = new Set(points.map((p) => p.lineId)).size;
    containerEl.innerHTML = `
      <div class="toolset-group" open>
        <div class="toolset-content">
          <div class="small" style="font-weight:700;">Multibeam Patch Test Optimizer</div>
          <div class="small" style="margin-top:4px; opacity:.76;">Calibration-grade patch testing needs raw sonar geometry, attitude, navigation, and timing. XYZ runs here are residual diagnostics only.</div>

          <div style="margin-top:10px; display:grid; gap:8px;">
            <label class="small" style="display:block;">
              <span style="display:block; margin-bottom:4px;">Patch line files</span>
              <input id="pt-files" type="file" multiple accept=".csv,.txt,.xyz,.pts,.json,.gsf,.hsx,.all,.kmall,.s7k,.xtf,.raw" style="width:100%;" />
            </label>
            <div class="row lm-add-row">
              <button id="pt-load" class="btn"${busy ? " disabled" : ""}>Load Lines</button>
              <button id="pt-run" class="btn primary"${busy || points.length < 100 || lineCount < 2 ? " disabled" : ""}>Run XYZ Diagnostic</button>
              <button id="pt-export" class="btn"${busy || !results ? " disabled" : ""}>Export</button>
              <button id="pt-clear" class="btn"${busy || (!points.length && !files.length && !results) ? " disabled" : ""}>Clear</button>
            </div>
          </div>

          <div style="margin-top:10px; display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:8px;">
            ${numberInput("pt-cell-size", "Cell", state.cellSize, "0.05", "5000", "0.1")}
            ${numberInput("pt-max-points", "Max Pts", state.maxPoints, "1000", "1000000", "1000")}
            ${numberInput("pt-trim", "Trim %", state.robustTrimPct, "0", "45", "1")}
          </div>
          <div style="margin-top:8px; display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:8px;">
            ${numberInput("pt-coarse-range", "Coarse +/-deg", state.coarseRange, "0.1", "45", "0.1")}
            ${numberInput("pt-coarse-step", "Coarse Step", state.coarseStep, "0.01", "10", "0.01")}
            ${numberInput("pt-fine-range", "Fine +/-deg", state.fineRange, "0.01", "10", "0.01")}
            ${numberInput("pt-fine-step", "Fine Step", state.fineStep, "0.001", "2", "0.001")}
            ${numberInput("pt-final-range", "Final +/-deg", state.finalRange, "0.001", "1", "0.001")}
            ${numberInput("pt-final-step", "Final Step", state.finalStep, "0.0001", "0.5", "0.0001")}
          </div>

          <div class="small" style="margin-top:10px; color:${busy ? "#7dd3fc" : "#cbd5e1"};">${esc(status)}</div>
          <div class="small" style="margin-top:4px; opacity:.76;">Parsed points: ${points.length.toLocaleString()} | Lines: ${lineCount} | Files: ${files.length}</div>

          ${renderRawWorkflow()}
          ${renderFormatNote()}
          ${renderFileTable()}
          ${renderResults()}
        </div>
      </div>
    `;
  }

  function numberInput(id, label, value, min, max, step) {
    return `
      <label class="small" style="display:block;">
        <span style="display:block; margin-bottom:4px;">${esc(label)}</span>
        <input id="${esc(id)}" type="number" min="${esc(min)}" max="${esc(max)}" step="${esc(step)}" value="${esc(value)}" style="width:100%;" />
      </label>
    `;
  }


  function renderRawWorkflow() {
    const q = converterStatus;
    const supported = ["ALL", "KMALL", "S7K"].map((name) => `<span style="display:inline-block; margin-right:4px; padding:2px 6px; border-radius:999px; background:rgba(34,197,94,.16); color:#bbf7d0;">${name}</span>`).join("");
    const needs = ["GSF", "HSX", "XTF", "RAW"].map((name) => `<span style="display:inline-block; margin-right:4px; padding:2px 6px; border-radius:999px; background:rgba(251,191,36,.14); color:#fde68a;">${name}</span>`).join("");
    return `
      <div style="margin-top:10px; border:1px solid rgba(148,163,184,.22); border-radius:8px; padding:8px;">
        <div class="small" style="font-weight:700;">Raw-Sonar Path</div>
        <div class="small" style="margin-top:6px; line-height:1.45;">
          Existing bridge: ${supported}<br />
          Decoder work needed: ${needs}
        </div>
        <div class="row lm-add-row" style="margin-top:8px;">
          <button id="pt-check-converters" class="btn"${busy ? " disabled" : ""}>Check Converters</button>
        </div>
        ${q ? `<div class="small" style="margin-top:6px; opacity:.76;">${esc(q.error ? q.error : summarizeConverterStatus(q))}</div>` : ""}
      </div>
    `;
  }

  function summarizeConverterStatus(payload) {
    const q = payload?.qimera || payload;
    const path = q?.executablePath || q?.qimeraCommandPath || q?.path || "";
    if (path) return `Qimera-command: ${path}`;
    if (q?.installed === false) return "Qimera-command not found.";
    return "Converter status loaded.";
  }

  function renderFormatNote() {
    return `
      <div class="small" style="margin-top:10px; padding:8px; border:1px solid rgba(148,163,184,.22); border-radius:8px; background:rgba(15,23,42,.24); line-height:1.35;">
        Current diagnostic ingest: CSV/XYZ/PTS/JSON with x,y,z plus optional line_id, along, and across columns. True patch-test ingest starts with raw formats: ALL/KMALL/S7K through the existing Qimera bridge, GSF through MB-System or a native reader, and HSX/XTF/RAW through vendor-specific decoders or export workflows.
      </div>
    `;
  }

  function renderFileTable() {
    if (!files.length) return "";
    return `
      <div style="margin-top:10px; max-height:150px; overflow:auto; border:1px solid rgba(148,163,184,.22); border-radius:8px;">
        <table style="width:100%; border-collapse:collapse; font-size:11px;">
          <thead><tr><th style="text-align:left; padding:5px;">File</th><th style="text-align:right; padding:5px;">Pts</th><th style="text-align:left; padding:5px;">Status</th></tr></thead>
          <tbody>
            ${files.map((file) => `
              <tr>
                <td style="padding:5px; border-top:1px solid rgba(148,163,184,.12);">${esc(file.name)}</td>
                <td style="padding:5px; border-top:1px solid rgba(148,163,184,.12); text-align:right;">${Number(file.points || 0).toLocaleString()}</td>
                <td style="padding:5px; border-top:1px solid rgba(148,163,184,.12);">${esc(file.status)}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    `;
  }

  function renderResults() {
    if (!results) return "";
    return `
      <div style="margin-top:12px;">
        <div class="small" style="font-weight:700;">Apparent XYZ Offsets</div>
        <div style="margin-top:6px; display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:8px;">
          ${offsetCell("Roll", results.offsets.roll)}
          ${offsetCell("Pitch", results.offsets.pitch)}
          ${offsetCell("Yaw", results.offsets.yaw)}
        </div>
        <div class="small" style="margin-top:8px; opacity:.76;">Final RMSE: ${fmt(results.finalScore.rmse)} | MAD: ${fmt(results.finalScore.mad)} | Overlap cells: ${Number(results.finalScore.overlapCells || 0).toLocaleString()}</div>
        ${results.axisResults.map(renderAxisResult).join("")}
      </div>
    `;
  }

  function offsetCell(label, value) {
    return `
      <div style="border:1px solid rgba(148,163,184,.22); border-radius:8px; padding:8px;">
        <div class="small" style="opacity:.72;">${esc(label)}</div>
        <div style="font-size:18px; font-weight:700; margin-top:2px;">${fmt(value)} deg</div>
      </div>
    `;
  }

  function renderAxisResult(axisResult) {
    const final = axisResult.scans[axisResult.scans.length - 1];
    return `
      <div style="margin-top:12px;">
        <div class="small" style="font-weight:700; text-transform:capitalize;">${esc(axisResult.axis)} Scan</div>
        ${renderMiniChart(final.samples, final.best)}
        <div class="small" style="margin-top:4px; opacity:.72;">Best ${esc(final.name)} sample: ${fmt(final.best?.angle)} deg, RMSE ${fmt(final.best?.rmse)}, residuals ${Number(final.best?.residuals || 0).toLocaleString()}.</div>
      </div>
    `;
  }

  function renderMiniChart(samples, best) {
    if (!samples?.length) return "";
    const finite = samples.filter((s) => Number.isFinite(s.score));
    if (!finite.length) return `<div class="small" style="margin-top:6px; opacity:.72;">No overlapping cells found.</div>`;
    const min = Math.min(...finite.map((s) => s.score));
    const max = Math.max(...finite.map((s) => s.score));
    const span = Math.max(1e-12, max - min);
    return `
      <div style="height:74px; margin-top:6px; display:flex; align-items:flex-end; gap:2px; border:1px solid rgba(148,163,184,.2); border-radius:8px; padding:6px;">
        ${samples.map((sample) => {
          const h = Number.isFinite(sample.score) ? 8 + ((sample.score - min) / span) * 54 : 4;
          const isBest = sample.angle === best?.angle;
          return `<div title="${esc(sample.angle)} deg / RMSE ${esc(fmt(sample.rmse))}" style="flex:1; min-width:2px; height:${h}px; background:${isBest ? "#22c55e" : "rgba(125,211,252,.68)"}; border-radius:2px 2px 0 0;"></div>`;
        }).join("")}
      </div>
    `;
  }

  function mount(el) {
    containerEl = el;
    containerEl.addEventListener("click", onClick);
    render();
  }

  function destroy() {
    if (!containerEl) return;
    containerEl.removeEventListener("click", onClick);
    containerEl = null;
  }

  function snapshot() {
    return { version: 1, state: { ...state } };
  }

  function restore(data) {
    state = sanitizeState({ ...state, ...(data?.state || {}) });
    persist();
    render();
  }

  return { mount, destroy, snapshot, restore };
}
